import React from 'react';
import { motion } from 'framer-motion';
import { 
  Mic, BarChart3, LayoutDashboard, BrainCircuit, Link2, Mail,
  CheckCircle2, Sparkles
} from 'lucide-react';

const pillars = [
  {
    icon: Mic,
    badge: "🧠",
    title: "Zero-Touch CRM",
    tagline: "Your reps never log another call.",
    color: "from-primary to-primary/70",
    features: [
      "Auto-captures meetings from Google Calendar/Meet",
      "Extracts action items, next steps & sentiment from transcripts",
      "Updates HubSpot deal properties, stages & notes automatically",
      "Safe actions execute autonomously; risky ones need one-click approval",
      "Full undo capability with audit trail",
    ],
  },
  {
    icon: BarChart3,
    badge: "📊",
    title: "Evidence-Based Forecasting",
    tagline: "Know what's going to close before the board asks.",
    color: "from-green-500 to-green-400",
    features: [
      "AI-scored deal confidence based on actual buyer signals",
      "MEDDIC/qualification scoring auto-populated from conversations",
      "Forecast buckets (Commit / Best Case / Pipeline) backed by evidence",
      "Historical backtesting to calibrate accuracy over time",
      "Replaces Clari and manual forecast spreadsheets",
    ],
  },
  {
    icon: LayoutDashboard,
    badge: "🎯",
    title: "Command Center for Leaders",
    tagline: "Your morning briefing, every morning.",
    color: "from-blue-500 to-blue-400",
    features: [
      "At-risk deals surfaced with specific reasons & recommended actions",
      "Pipeline coverage analysis and gap identification",
      "Rep performance rankings with activity metrics",
      "Competitive intelligence aggregated across all deals",
      "Action items you can assign and track to resolution",
    ],
  },
  {
    icon: BrainCircuit,
    badge: "🤖",
    title: "AI Sales Coach",
    tagline: "A coach in every rep's ear — before, during & after every meeting.",
    color: "from-purple-500 to-purple-400",
    features: [
      "Pre-meeting briefs with deal context & stakeholder map",
      "Real-time voice assistant for live meeting support",
      "Post-meeting summaries with auto-generated follow-up drafts",
      "Personalized coaching based on win/loss patterns",
      "Objection handling pulled from competitive battlecards",
    ],
  },
  {
    icon: Link2,
    badge: "🔗",
    title: "Deep HubSpot Integration",
    tagline: "Not another tool on top of HubSpot. The brain inside it.",
    color: "from-hubspot to-orange-400",
    features: [
      "Bi-directional sync with full field mapping",
      "Respects HubSpot permissions — no rogue writes",
      "Works with your existing pipeline stages and properties",
      "Contact, Company & Deal enrichment from conversations",
      "Association management (contacts ↔ deals ↔ companies)",
    ],
  },
  {
    icon: Mail,
    badge: "📧",
    title: "Google Workspace Native",
    tagline: "Connects to where your reps already live.",
    color: "from-red-500 to-red-400",
    features: [
      "Calendar, Meet & Gmail integration",
      "Automatic transcript ingestion from Google Meet",
      "Email thread analysis for deal signals",
      "No behaviour change required from reps",
    ],
  },
];

export const FeaturePillars = () => {
  return (
    <section className="w-full py-20 md:py-32 bg-background">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Platform Capabilities
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Everything your revenue team needs.{' '}
            <span className="text-gradient-teal">Nothing they don't.</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Six pillars that turn your CRM from a data graveyard into your competitive advantage.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              viewport={{ once: true }}
              className="group bg-card rounded-2xl border border-border p-6 hover:border-primary/30 hover:shadow-card transition-all duration-300"
            >
              {/* Header */}
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <pillar.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">{pillar.title}</h3>
                  <p className="text-sm text-primary italic">{pillar.tagline}</p>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2.5">
                {pillar.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
